import React, { useState } from 'react';
import { Box, Stack, Chip, Button, Typography, Tooltip } from '@mui/material';
import { Add, ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import FormInput from '../inputs/FormInput';

/**
 * Chip input for the `variables` array on a notification template. Each chip
 * is a placeholder name (e.g. "customer_name") that can later be referenced
 * in subject/body via {{customer_name}}. Click a chip to copy the {{...}}
 * form to the clipboard so the operator can paste it into the body field.
 */
const VariablesEditor = ({ value = [], onChange, disabled }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const [draft, setDraft] = useState('');

    const variables = Array.isArray(value) ? value : [];

    const addVariable = () => {
        const v = draft.trim().replace(/[{}]/g, '');
        if (!v || variables.includes(v)) {
            setDraft('');
            return;
        }
        onChange([...variables, v]);
        setDraft('');
    };

    const removeVariable = (name) => {
        onChange(variables.filter((v) => v !== name));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addVariable();
        }
    };

    const copyPlaceholder = async (name) => {
        const placeholder = `{{${name}}}`;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(placeholder);
            } else {
                // Fallback for older browsers / non-secure contexts
                const ta = document.createElement('textarea');
                ta.value = placeholder;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            toast.success(
                isArabic
                    ? `تم نسخ ${placeholder}`
                    : `Copied ${placeholder}`
            );
        } catch {
            toast.error(isArabic ? 'تعذر النسخ' : 'Failed to copy');
        }
    };

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {isArabic
                    ? 'متغيرات يمكن استخدامها داخل العنوان والمحتوى بصيغة {{اسم_المتغير}}. اضغط على أي متغير لنسخ صيغته إلى الحافظة.'
                    : 'Variables you can reference inside the subject and body using {{name}}. Click any chip to copy its placeholder to the clipboard.'}
            </Typography>

            <Stack direction="row" spacing={1} mb={1.5} alignItems="flex-end">
                <Box sx={{ flex: 1 }}>
                    <FormInput
                        type="text"
                        name="variable_draft"
                        placeholder={isArabic ? 'مثال: customer_name' : 'e.g. customer_name'}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={disabled}
                        required={false}
                    />
                </Box>
                <Button
                    variant="contained"
                    onClick={addVariable}
                    startIcon={<Add />}
                    disabled={disabled || !draft.trim()}
                    sx={{ height: 40 }}
                >
                    {isArabic ? 'إضافة' : 'Add'}
                </Button>
            </Stack>

            {variables.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {isArabic ? 'لا توجد متغيرات بعد' : 'No variables yet'}
                </Typography>
            ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {variables.map((name) => (
                        <Tooltip
                            key={name}
                            title={isArabic ? 'اضغط للنسخ' : 'Click to copy'}
                            arrow
                            placement="top"
                        >
                            <Chip
                                label={`{{${name}}}`}
                                icon={<ContentCopy fontSize="small" />}
                                onClick={() => copyPlaceholder(name)}
                                onDelete={disabled ? undefined : () => removeVariable(name)}
                                color="primary"
                                variant="tonal"
                                sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    '& .MuiChip-icon': {
                                        marginInlineStart: '6px',
                                    },
                                }}
                            />
                        </Tooltip>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default VariablesEditor;
